// src/utils/logger.js
//
// Centralized logger. Replaces ad-hoc console.log("[API] ...") calls
// scattered through the monolith. Single seam for:
//   - DEV_MODE gating (silence debug logs in production)
//   - PII / sensitive-field scrubbing at the boundary
//   - Future ship to Sentry, Datadog, or Replit's log shipper
//
// Per CLAUDE.md observability rules, all log messages use a prefix and
// stay terse. No debug `console.log` should leak past production builds.

// ─── Configuration ────────────────────────────────────────────────────────

const LEVELS = { debug: 0, info: 1, warn: 2, error: 3 };

// Vite injects this from import.meta.env.DEV at build time.
// During the monolith era we read window.__DEV_MODE__ (set near the top of
// the HTML launcher) so this module works in both contexts.
const DEV_MODE = (typeof window !== "undefined" && window.__DEV_MODE__ === true);
const MIN_LEVEL = DEV_MODE ? LEVELS.debug : LEVELS.warn;

// ─── PII / sensitive-field scrubbing ──────────────────────────────────────
//
// Rules:
//   - Field names matching SCRUB_KEYS are replaced with "[redacted]".
//   - Field names matching MASK_MONEY are replaced with the type/currency
//     but not the amount, so logs still show shape ("USD amount").
//   - Field names matching DROP_PII are removed entirely from the log.
//
// The scrub runs at the LOGGER boundary only. Audit-log envelopes go to the
// server with the raw before/after intact, since the audit_log table is
// admin-read-only. See src/services/auditLog.js.

const SCRUB_KEYS  = /^(token|secret|password|api_?key|session_?id|authorization|cookie|jwt|bearer)$/i;
const MASK_MONEY  = /^(totalCost|tiaTotalAmount|securityDepositAmount|totalAmount|drawnToDate|amount|budget|opex|cam|propertyTax)$/;
const DROP_PII    = /^(badges|badgeData|individualBadges|email|phoneNumber|ssn)$/;

function scrub(obj, depth = 0) {
  if (depth > 8) return "[max-depth]";
  if (obj == null) return obj;
  if (typeof obj !== "object") return obj;
  if (obj instanceof Date) return obj.toISOString();
  if (Array.isArray(obj)) return obj.map((v) => scrub(v, depth + 1));

  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (DROP_PII.test(k)) continue;
    if (SCRUB_KEYS.test(k)) { out[k] = "[redacted]"; continue; }
    if (MASK_MONEY.test(k) && typeof v === "number") {
      out[k] = `[money:${obj.currency || "?"}]`;
      continue;
    }
    out[k] = (v && typeof v === "object") ? scrub(v, depth + 1) : v;
  }
  return out;
}

// ─── Emission ─────────────────────────────────────────────────────────────

function emit(level, prefix, message, meta) {
  if (LEVELS[level] < MIN_LEVEL) return;
  const fn = level === "debug" ? "log" : level;
  const args = [`[${prefix}]`, message];
  if (meta !== undefined) args.push(scrub(meta));
  console[fn](...args);

  // Future: ship to Sentry / Replit logs. Keep this seam minimal so we don't
  // double-send during the transition.
  if (typeof window !== "undefined" && window.__logSink__) {
    try { window.__logSink__({ level, prefix, message, meta: scrub(meta), at: new Date().toISOString() }); }
    catch { /* swallow; sink errors must never crash the app */ }
  }
}

// ─── Public API ───────────────────────────────────────────────────────────

export const log = {
  // Use these in the right place - the prefix is part of the contract.
  api:     (msg, meta) => emit("info",  "API",     msg, meta),
  reducer: (msg, meta) => emit("debug", "Reducer", msg, meta),
  ui:      (msg, meta) => emit("info",  "UI",      msg, meta),
  audit:   (msg, meta) => emit("info",  "Audit",   msg, meta),
  error:   (msg, meta) => emit("error", "Error",   msg, meta),
  test:    (msg, meta) => emit("debug", "Test",    msg, meta),
  warn:    (msg, meta) => emit("warn",  "Warn",    msg, meta),
};

// Exposed for self-tests and one-off debugging.
export const __scrubForTests = scrub;
